import React, {useCallback} from 'react';
import {Client as AuthClient} from '@apps-common/auth';
import {AuthProvider} from '@apps-common/auth-react';
import {CssBaseline} from '@mui/material';
import {DocumentHandler} from '@apps-common/auth-utils';
import {Helmet} from 'react-helmet';
import {ThemeProvider} from '@mui/material/styles';
import {setAuthToken} from '@apps-common/js-client/api/request';
import {setAuthToken as setGraneroAuthToken} from '@apps-common/geocorn-sdk/api';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import Explorer from './Explorer.jsx';
import NoQuotaBanner from './no-quota-banner.jsx';
import TopNavBar from '@explorer/components/navbars/top';
import {fetchBasemapRules} from '@explorer/actions/basemap-rules.js';
import {fetchItemTypes} from '@explorer/actions/item-types.js';
import {fetchSeriesRequest} from '@explorer/actions/series.js';
import {fetchSubscriptions} from '@explorer/actions/subscriptions';
import {getUserLabKeys} from '@explorer/constants/labs.js';
import {identify} from '@explorer/analytics.js';
import {lightTheme} from '../styles/index.jsx';
import {setLabStatus} from '@explorer/actions/labs.js';

const authClient = new AuthClient({
  clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
  redirectUri: `${window.location.origin}${window.location.pathname}`,
  postLogoutRedirectUri: `${window.location.origin}${window.location.pathname}`,
});

const App = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const storedLabs = useSelector(state => state.labs);

  const navigate = url => {
    window.history.pushState({}, '', url);
  };

  /**
   * @type {import('@apps-common/auth/client.js').ChangeListener}
   */
  const onAuthChange = useCallback(
    state => {
      setAuthToken(state.user?.accessToken || '');
      setGraneroAuthToken(state.user?.accessToken || '');

      if (state.user && !state.pending) {
        identify(state.user.claims);

        // this will block rendering until we have subscriptions
        dispatch(fetchSubscriptions());
        dispatch(fetchItemTypes());
        dispatch(fetchBasemapRules(state.user.accessToken)).then(() => {
          dispatch(fetchSeriesRequest());
        });

        // Users may have labs enabled in localStorage that they shouldn't have access to
        // This can occur if they enabled the lab in a different account on the same device, or if they edited their local store
        // This checks permissions when the user logs in, and disables labs that shouldn't be available
        const userLabs = getUserLabKeys(state.user);

        for (const labKey in storedLabs) {
          if (!userLabs.includes(labKey)) {
            dispatch(setLabStatus({labKey, enabled: false}));
          }
        }
      }
    },
    [dispatch, storedLabs]
  );

  return (
    <AuthProvider
      client={authClient}
      navigate={navigate}
      onChange={onAuthChange}
      private
    >
      <ThemeProvider theme={lightTheme}>
        <Helmet>
          <title>{t('common:meta.name')}</title>
          <meta itemProp="name" content={t('common:meta.name')} />
          <meta property="og:title" content={t('common:meta.name')} />
          <meta property="og:description" content={t('common:meta.tagline')} />
          <meta property="og:url" content={t('common:meta.url')} />
        </Helmet>
        <CssBaseline />
        <NoQuotaBanner />
        <TopNavBar />
        <DocumentHandler>
          <Explorer />
        </DocumentHandler>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
